import { useTranslation } from 'react-i18next';
import {
  ArrowLeftRight,
  CalendarCheck,
  Copy,
  FileText,
  MapPin,
  MessageCircle,
  Phone,
  Sparkles,
  TriangleAlert,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';

import { formatDateTime } from '@/lib/format';
import type { ActivityType, LeadActivity } from '@/types/app';

const ICONS: Partial<Record<ActivityType, LucideIcon>> = {
  lead_created: Sparkles,
  assigned: UserPlus,
  reassigned: ArrowLeftRight,
  call: Phone,
  whatsapp: MessageCircle,
  note: FileText,
  meeting: CalendarCheck,
  site_visit: MapPin,
  stage_changed: ArrowLeftRight,
  sla_breach: TriangleAlert,
  duplicate: Copy,
};

const TONE: Partial<Record<ActivityType, string>> = {
  sla_breach: 'text-red-600',
  duplicate: 'text-amber-600',
  reassigned: 'text-violet-600',
  stage_changed: 'text-indigo-600',
};

/**
 * The lead timeline (#10).
 *
 * Shows before/after for stage changes explicitly rather than only the free-text
 * body, because the acceptance criterion is that a stage change is visible *with*
 * its previous and new value — a line reading "moved to follow_up" does not let
 * anyone reconstruct what happened.
 */
export function Timeline({ entries }: { entries: LeadActivity[] }) {
  const { t } = useTranslation();

  if (entries.length === 0) {
    return <p className="text-sm text-slate-500">{t('leads.noActivity')}</p>;
  }

  return (
    <ol className="flex flex-col gap-3">
      {entries.map((entry) => {
        const Icon = ICONS[entry.type] ?? FileText;
        const stageFrom = entry.before?.['stage'];
        const stageTo = entry.after?.['stage'];

        return (
          <li key={entry.id} className="flex gap-3">
            <div className="mt-0.5 shrink-0">
              <Icon
                className={`size-4 ${TONE[entry.type] ?? 'text-slate-400'}`}
                aria-hidden="true"
              />
            </div>

            <div className="min-w-0 flex-1 border-b border-slate-100 pb-3">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-sm font-medium text-slate-800">
                  {t(`activities.${entry.type}` as 'activities.call')}
                </span>
                {entry.outcome ? (
                  <span className="text-xs text-slate-500">
                    {t(`callOutcomes.${entry.outcome}` as 'callOutcomes.answered')}
                  </span>
                ) : null}
                <span className="text-xs text-slate-400">{formatDateTime(entry.occurred_at)}</span>
              </div>

              {typeof stageFrom === 'string' && typeof stageTo === 'string' ? (
                <p className="mt-0.5 text-sm text-slate-600">
                  {t(`stages.${stageFrom}` as 'stages.new')}
                  {' → '}
                  {t(`stages.${stageTo}` as 'stages.new')}
                </p>
              ) : null}

              {/* System activities carry structured data, not prose, so their detail
                  is rendered from the locale files. Storing a sentence in the row
                  would make it permanently untranslatable. */}
              {typeof entry.after?.['source'] === 'string' ? (
                <p className="mt-0.5 text-sm text-slate-600">
                  {t('leads.capturedFrom', {
                    source: t(`sources.${entry.after['source']}` as 'sources.meta'),
                  })}
                </p>
              ) : null}

              {typeof entry.after?.['method'] === 'string' ? (
                <p className="mt-0.5 text-sm text-slate-600">
                  {t('leads.assignedBy', { method: entry.after['method'] })}
                </p>
              ) : null}

              {entry.body ? (
                <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-600">{entry.body}</p>
              ) : null}

              {entry.actor_name ? (
                <p className="mt-0.5 text-xs text-slate-400">{entry.actor_name}</p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
